import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { chatAPI } from '../services/api';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import logger from '../utils/logger';
import './ChatWidget.css';

const ChatWidget = () => {
  const { isAuthenticated, user } = useAuth();
  const {
    conversations,
    activeChat,
    messages,
    loading,
    error: chatError,
    createOrGetChat,
    selectChat,
    sendMessage,
    sendTyping,
    getTotalUnreadCount,
    fetchConversations,
    socket,
    setError
  } = useChat();

  const [isOpen, setIsOpen] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [localError, setLocalError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const unreadCount = getTotalUnreadCount();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, activeChat]);

  // Handle opening chat
  const handleOpenChat = useCallback(async () => {
    setIsOpen(true);
    
    try {
      if (!activeChat && conversations.length === 0) {
        // Create new chat if none exists
        const newChat = await createOrGetChat();
        if (newChat) {
          await selectChat(newChat);
        } else {
          // If creation failed, try fetching conversations again
          await fetchConversations();
          if (conversations.length > 0) {
            await selectChat(conversations[0]);
          }
        }
      } else if (!activeChat && conversations.length > 0) {
        // Select first conversation - this will fetch messages
        await selectChat(conversations[0]);
      } else if (activeChat) {
        // If chat is already active, ensure we're in the room and re-fetch messages
        if (socket) {
          const chatId = activeChat._id?.toString() || activeChat._id;
          socket.emit('join-chat', chatId);
          logger.debug('📥 Re-joined chat room:', chatId);
          // Re-select the chat to refresh messages
          await selectChat(activeChat);
        }
      }
    } catch (error) {
      logger.error('Error opening chat:', error);
      setLocalError('Failed to open chat. Please try again.');
    }
  }, [activeChat, conversations, createOrGetChat, selectChat, fetchConversations, socket]);

  // Listen for custom event to open chat from other components
  useEffect(() => {
    const handleOpenChatEvent = () => {
      handleOpenChat();
    };

    window.addEventListener('openChatWidget', handleOpenChatEvent);
    return () => {
      window.removeEventListener('openChatWidget', handleOpenChatEvent);
    };
  }, [handleOpenChat]);

  // Handle sending message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageInput.trim() || !activeChat) return;

    sendMessage(messageInput);
    setMessageInput('');
    setIsTyping(false);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    sendTyping(false);
  };

  // Handle typing indicator
  const handleInputChange = (e) => {
    setMessageInput(e.target.value);
    
    if (!isTyping && e.target.value.trim()) {
      setIsTyping(true);
      sendTyping(true);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTyping(false);
    }, 2000);
  };

  // Get other participant name
  // Since users message all admins, show generic admin name
  const getOtherParticipantName = () => {
    return "Batino's Garden Admin";
  };

  // Format message time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Only show for regular users (not admins), and require verified email
  if (!isAuthenticated || user?.role === 'admin') return null;

  const handleChatButtonClick = () => {
    if (!user?.emailVerified) {
      alert('Please verify your email address before messaging the admin. You can verify it in your profile settings.');
      return;
    }
    handleOpenChat();
  };

  return (
    <>
      {/* Chat Button */}
      <button
        className="chat-widget-button"
        onClick={handleChatButtonClick}
        aria-label="Open chat"
      >
        <MessageCircle size={24} />
        {unreadCount > 0 && (
          <span className="chat-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-window-header">
            <div className="chat-header-info">
              <h3>{getOtherParticipantName()}</h3>
              {socket?.connected && (
                <span className="chat-status online">Live chat connected</span>
              )}
            </div>
            <button
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          <div className="chat-messages-container">
            {(chatError || localError) && (
              <div className="chat-error">
                <p>{chatError || localError}</p>
                <button onClick={() => { setError(null); setLocalError(null); }}>Dismiss</button>
              </div>
            )}
            {!socket?.connected && (
              <div className="chat-error">
                <p>Not connected to chat server. Trying to reconnect...</p>
              </div>
            )}
            {loading && messages.length === 0 ? (
              <div className="chat-loading">
                <Loader2 className="spinning" size={24} />
                <p>Loading messages...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-empty">
                <MessageCircle size={48} />
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              <div className="chat-messages">
                {messages.map((message) => {
                  const messageId = message._id?.toString() || message._id || message.tempId;
                  const senderId = message.sender?._id?.toString() || message.sender?._id;
                  const userId = user?._id?.toString() || user?._id;
                  const isOwnMessage = senderId === userId;
                  const isSystemMessage = message.type === 'system';
                  
                  return (
                    <div
                      key={messageId}
                      className={`chat-message ${isSystemMessage ? 'system' : isOwnMessage ? 'own' : 'other'}`}
                    >
                      <div className="message-content">
                        {isSystemMessage && (
                          <div className="system-message-label">Automated Reply</div>
                        )}
                        <p>{message.content}</p>
                        <span className="message-time">{formatTime(message.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <form className="chat-input-container" onSubmit={handleSendMessage}>
            <input
              ref={inputRef}
              type="text"
              value={messageInput}
              onChange={handleInputChange}
              placeholder="Type a message..."
              className="chat-input"
              disabled={!activeChat || !socket?.connected}
            />
            <button
              type="submit"
              className="chat-send-btn"
              disabled={!messageInput.trim() || !activeChat || !socket?.connected}
            >
              <Send size={20} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;

